<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { ref, computed } from "vue";
import Modal from "@/components/modals/ModalContainer.vue";
import { useRouter } from "vue-router";
import { onboardingApi } from "~/api/onboarding";
import { useStoreApp } from "~/stores/storeApp";
const router = useRouter();
const { t, locale } = useI18n();

import { useStoreUserV2 } from "~/stores/storeUserV2";
import Trans from "~/modules/translations";

const userStore = useStoreUserV2();

const emit = defineEmits<{
  (event: "goBack"): void;
  (event: "onClose"): void;
}>();

const handleCloseModal = () => {
  onboardingApi.hideOnboarding();
  emit("onClose");
};

const props = withDefaults(
  defineProps<{
    show?: boolean;
  }>(),
  {
    show: true,
  }
);

const guideLink = computed(() => {
  const isEn = locale.value === "en" ? true : false;
  if (isEn) return "https://guide.fungiball.io/en";
  return "https://guide.fungiball.io";
});

const currentStep = ref(0);

const steps = computed(() => {
  let steps = [
    {
      title: t("rg2025.step1.title", { username: userStore.username }),
      visual: {
        img: "/images/onboardingStep1.jpg",
        alt: "Fungiball",
      },
      paragraphs: [
        {
          txt: t("rg2025.step1.p1"),
        },
        {
          txt: t("rg2025.step1.p2"),
        },
        {
          txt: t("rg2025.step1.p3"),
        },
        {
          txt: t("rg2025.step1.p4"),
        },
      ],
    },
  ];
  return steps;
});

const currentStepData = computed(() => {
  return steps.value[currentStep.value];
});

const handleGoToNextStep = () => {
  if (currentStep.value < steps.value.length - 1) {
    currentStep.value++;
  } else {
    handleCloseModal();
  }
};

const storeApp = useStoreApp();
async function signUp() {
  storeApp.showRG2025Modal = false;
  router.push(Trans.i18nRoute({ name: "SignUp" }));
}
</script>

<template>
  <Modal
    bgColor="bg-black"
    modalClasses=""
    :show="storeApp.initialized"
    :showCloseButton="false"
    size="lg"
    closeButtonClasses=""
    :headerIMG="currentStepData.visual.img"
  >
    <TransitionGroup name="slide" tag="div" class="relative min-h-[420px]">
      <div
        v-for="(step, index) in steps"
        :key="index"
        v-show="currentStep === index"
        class="step-content w-full"
      >
        <div class="flex flex-col gap-2 w-full">
          <div class="text-3xl mb-3 font-semibold">
            {{ currentStepData.title }}
          </div>

          <div v-for="(paragraph, pIndex) in step.paragraphs" :key="pIndex" class="mb-4">
            <p v-if="paragraph.txt" v-html="paragraph.txt"></p>
            <div v-else class="w-full items-center justify-center gap-4 flex flex-col">
              <div class="md:px-24 px-10 w-full mt-2"></div>
            </div>
          </div>
        </div>
      </div>
    </TransitionGroup>

    <template #footer>
      <div class="absolute top-0 left-0 right-0 h-1"></div>
      <div class="w-full items-center gap-4 px-4 flex my-4 justify-between">
        <Button type="secondary" class="w-full" @click="handleGoToNextStep">
          {{ t("_shared.close") }}
        </Button>
        <Button type="primary-blue" class="w-full" @click="signUp">
          {{ t("pages.auth.form.signup.submit") }}
        </Button>
      </div>
    </template>
  </Modal>
</template>

<style scoped>
.step-content {
  position: absolute;
  width: 100%;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.5s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-enter-to,
.slide-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
